import { selectPendingFollowUp } from './shared-responder-v2-core-follow-up-prompt.helpers';
import type {
  QuestionLiveResultsResult,
  QuestionTurnResult,
} from './shared-responder-v2-core-gateway.types';
import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';
import {
  THREAD_STATUSES,
  TURN_INTENTS,
  TURN_ROLES,
} from './shared-responder-v2-core-thread.consts';
import type {
  ResponderAnswer,
  ResponderThread,
  ResponderTurn,
  ResponderTurnPrompt,
} from './shared-responder-v2-core-thread.types';

function makeTurn(
  questionId: string,
  intent: ResponderTurn['intent'],
  role: ResponderTurn['role'],
  text: string,
  index: number
): ResponderTurn {
  return {
    id: `${questionId}:${intent}:${index}`,
    role,
    intent,
    text,
    transcript: null,
    prompt: null,
  };
}

/**
 * A bubble for what the responder said, or `null` for a selection answer.
 *
 * Only text answers get a turn: a selection *is* the on-screen answer for its
 * own surface, so it needs no bubble.
 */
export function makeResponderAnswerTurn(
  questionId: string,
  answer: ResponderAnswer,
  intent: ResponderTurn['intent'],
  index: number
): ResponderTurn | null {
  if (answer.kind !== 'text') {
    return null;
  }

  return {
    ...makeTurn(questionId, intent, TURN_ROLES.Responder, answer.text, index),
    transcript: answer.transcript,
  };
}

function resolveSelectedOptionValues(
  answer: ResponderAnswer
): readonly string[] | null {
  if (answer.kind === 'singleSelect') {
    return [answer.optionValue];
  }

  if (answer.kind === 'multiSelect') {
    return answer.optionValues;
  }

  return null;
}

function resolveOptionLabel(
  prompt: ResponderTurnPrompt,
  value: string
): string | null {
  const index = prompt.optionValues.indexOf(value);

  return index === -1 ? null : (prompt.optionLabels[index] ?? value);
}

/** The turn renders the option's label; `answer` still carries its value for the wire. */
export function makeFollowUpAnswerTurn(
  questionId: string,
  answer: ResponderAnswer,
  prompt: ResponderTurnPrompt | null,
  index: number
): ResponderTurn | null {
  if (answer.kind === 'text') {
    return {
      ...makeTurn(
        questionId,
        TURN_INTENTS.FollowUpAnswer,
        TURN_ROLES.Responder,
        answer.text,
        index
      ),
      transcript: answer.transcript,
    };
  }

  if (prompt === null) {
    return null;
  }

  const selectedValues = resolveSelectedOptionValues(answer);

  if (selectedValues === null || selectedValues.length === 0) {
    return null;
  }

  const label = selectedValues
    .map((value) => resolveOptionLabel(prompt, value))
    .filter((value): value is string => value !== null)
    .join(', ');

  if (label === '') {
    return null;
  }

  return makeTurn(
    questionId,
    TURN_INTENTS.FollowUpAnswer,
    TURN_ROLES.Responder,
    label,
    index
  );
}

/**
 * Records the responder's own answer in the thread and empties the composer.
 *
 * Both halves are one change on purpose. Nothing else appended a
 * `TURN_ROLES.Responder` turn, so an answered question showed only the question
 * and the AI's reply — the responder could not see what they had just said, and
 * the sole trace of it was the text still sitting in the composer. Clearing the
 * draft without recording the turn would make the answer vanish; recording it
 * without clearing leaves a primed composer whose Send re-posts the same answer
 * (`addQuestionMessage` is not idempotent).
 */
export function appendResponderTurn(thread: ResponderThread): ResponderThread {
  const { draft } = thread;

  if (draft === null) {
    return thread;
  }

  const hasPrimaryAnswer = thread.turns.some(
    (turn) =>
      turn.role === TURN_ROLES.Responder &&
      turn.intent === TURN_INTENTS.PrimaryAnswer
  );

  const turn = makeResponderAnswerTurn(
    thread.questionId,
    draft,
    hasPrimaryAnswer ? TURN_INTENTS.FollowUpAnswer : TURN_INTENTS.PrimaryAnswer,
    thread.turns.length
  );

  if (turn === null) {
    return thread;
  }

  return { ...thread, draft: null, turns: [...thread.turns, turn] };
}

/**
 * A follow-up the responder has not answered yet. The assistant asked a real
 * question, so no mode may advance past it — and reading it off the turns rather
 * than off the turn result's `reply` is what keeps a kind whose behaviour drops
 * follow-ups (`supportsFollowUps: false`) from being held back by a reply that
 * was never rendered.
 *
 * Scanning backwards for the responder's own turn — rather than testing the last
 * turn — is what makes it survive a trailing assistant bubble such as a
 * backend `replyFeedback`.
 */
export function hasUnansweredFollowUp(thread: ResponderThread): boolean {
  return selectPendingFollowUp(thread) !== null;
}

export function markFollowUpSkipped(thread: ResponderThread): ResponderThread {
  return { ...thread, isFollowUpSkipped: true, followUpDraft: null };
}

/**
 * Adopts the server's id for the answer the responder already sees.
 *
 * The optimistic turn was appended locally with a synthesised id; the backend
 * echoes the same message back with the id the resume read will use. Taking it
 * here is what makes a turn identical before and after a refresh, so nothing
 * downstream keyed on turn id has to special-case "not yet acknowledged".
 *
 * A selection answer has no local turn — its surface *is* the answer — so
 * there is nothing to reconcile and the echo is dropped rather than drawn as a
 * bubble no board shows.
 *
 * Only the *last* turn is a candidate, never an earlier one: every appender
 * (`appendResponderTurn`, `commitFollowUpReply`, `applyEditSubmission`) puts
 * the optimistic turn at the end, and a resumed history only ever arrives in
 * front of it. Scanning further back reached a turn the server had already
 * named — so a selection reply, which appends nothing, rewrote a *historic*
 * bubble's id and transcript with the new message's.
 */
function reconcileSentTurn(
  turns: readonly ResponderTurn[],
  sent: ResponderTurn | null
): ResponderTurn[] {
  const reconciled = [...turns];
  const lastIndex = reconciled.length - 1;
  const local = reconciled[lastIndex];

  if (
    sent === null ||
    local === undefined ||
    local.role !== TURN_ROLES.Responder
  ) {
    return reconciled;
  }

  reconciled[lastIndex] = {
    ...local,
    id: sent.id,
    transcript: local.transcript ?? sent.transcript,
  };

  return reconciled;
}

/**
 * Never drops a server turn, and never infers one either: whether another
 * follow-up is coming is the backend's `reply`/`isQuestionComplete` to state,
 * so no local depth arithmetic takes part in shaping the thread.
 */
export function appendAssistantTurns(
  thread: ResponderThread,
  result: QuestionTurnResult,
  liveResults: QuestionLiveResultsResult | null = null
): ResponderThread {
  const turns = reconcileSentTurn(thread.turns, result.sent);
  let followUpCount = thread.followUpCount;

  if (result.replyFeedback !== null) {
    turns.push(result.replyFeedback);
  }

  /**
   * A reply on a kind that nominally drops follow-ups is still rendered when
   * the backend holds the question open (`isComplete: false`): its cursor will
   * refuse every later question until this follow-up is answered
   * (`SURVEY_MESSAGES_V2_JUMP_NOT_ALLOWED`), so dropping the turn strands the
   * session with nothing on screen to answer.
   */
  const followUpQuestion =
    getThreadBehaviour(thread.kind).retainsServerFollowUps || !result.isComplete
      ? result.reply
      : null;

  if (followUpQuestion !== null) {
    turns.push(followUpQuestion);
    followUpCount += 1;
  }

  return {
    ...thread,
    turns,
    followUpCount,
    status: result.isComplete ? THREAD_STATUSES.Closed : THREAD_STATUSES.Open,
    liveResults: liveResults ?? thread.liveResults,
    // The first acknowledged message is the question's own answer; a follow-up
    // reply leaves it alone.
    answerMessageId: thread.answerMessageId ?? result.sent?.id ?? null,
    error: null,
    // The edit landed, so there is nothing left to roll back to — and keeping
    // the snapshot would make a *later*, unrelated failure restore an answer
    // the responder has since deliberately replaced.
    editRollback: null,
  };
}
